import axios from 'axios';
import { computed, ref } from 'vue';
import { useAiChatWidget } from '~/composables/useAiChatWidget';
import { useMainStore } from '~/stores/mainStore';
import { useTranslationStore } from '~/stores/translationStore';

interface Topic {
  id: number;
  name: string;
}

interface GlobalsBranding {
  welcome_message?: string;
  chatbot_name?: string;
  photo?: { url?: string };
  color?: { hex?: string };
  bubble_message?: string;
  bubble_message_enabled?: boolean;
  icon_size?: string;
  icon_position?: string;
}

interface Globals {
  favicon?: { url?: string };
  logo?: { url?: string };
  watermark?: unknown;
  info?: {
    school_name?: string;
    primary_color?: { hex?: string };
    [key: string]: unknown;
  };
  chat_configuration?: {
    branding?: GlobalsBranding;
    prompts?: unknown[];
    [key: string]: unknown;
  };
  contact_form_configuration?: {
    topics?: Topic[];
    default_email?: string;
    published?: boolean;
  } | null;
  [key: string]: unknown;
}

export const useAiChatClientInfo = () => {
  const mainStore = useMainStore();
  const translationStore = useTranslationStore();
  const { $t } = useNuxtApp();
  const { aiChatEnabled, tasksEnabled, displayChat, cmsContactFormEnabled } = useAiChatWidget();

  const language = computed(() => translationStore.locale);
  const languages = computed(() => translationStore.languages);
  const organizations = computed(() => mainStore.organizations);
  const globals = computed(() => mainStore.globals as unknown as Globals);
  const schoolName = computed(() => globals.value.info?.school_name);

  const chatConfigurationDefaults = computed(() => ({
    // this should always be sent as markdown for the compiler to work correctly in AI Chat
    welcomeMessage: $t('chatAndContact.drawer.chat.defaultWelcomeMessage', {
      orgName: globals.value.info?.school_name || '',
    }),
    bubbleMessage: $t('chatAndContact.drawer.chatBubble.defaultBubbleMessage', {
      orgName: globals.value.info?.school_name || '',
    }),
    chatbotName: $t('chatAndContact.drawer.chat.defaultChatbotName'),
    avatarUrl: globals.value.logo?.url || new URL('~/assets/sparkle_avatar_lg.svg', import.meta.url).href,
    color: globals.value.info?.primary_color?.hex || '#662E80',
  }));

  const chatConfiguration = computed(() => {
    const chatConfiguration = globals.value.chat_configuration || {};

    return {
      ...chatConfiguration,
      branding: {
        welcome_message: chatConfiguration?.branding?.welcome_message || chatConfigurationDefaults.value.welcomeMessage,
        chatbot_name: chatConfiguration?.branding?.chatbot_name || chatConfigurationDefaults.value.chatbotName,
        photo: { url: chatConfiguration?.branding?.photo?.url || chatConfigurationDefaults.value.avatarUrl },
        color: { hex: chatConfiguration?.branding?.color?.hex || chatConfigurationDefaults.value.color },
        bubble_message: chatConfiguration?.branding?.bubble_message || chatConfigurationDefaults.value.bubbleMessage,
        bubble_message_enabled: chatConfiguration?.branding?.bubble_message_enabled,
        icon_size: chatConfiguration?.branding?.icon_size,
        icon_position: chatConfiguration?.branding?.icon_position,
      },
      prompts: chatConfiguration?.prompts || [],
    };
  });

  const buttonsConfiguration = computed(() => {
    const buttons = [
      {
        name: 'inbox',
        enabled: tasksEnabled.value && contactFormConfiguration.value?.published && cmsContactFormEnabled.value,
      },
      {
        name: 'ai-chat',
        enabled: aiChatEnabled.value && displayChat.value,
      },
    ];
    return {
      enabled: buttons.filter((button) => button.enabled).map((button) => button.name),
    };
  });

  const fetchedTopics = ref<Topic[]>([]);

  /**
   * Fetches contact form topics from the task management API for the current secondary organization.
   * Falls back to an empty list on error so the contact form can still render.
   * Call when the user opens the Contact Us form (e.g. when switching to Inbox state).
   */
  const fetchTopics = async (secOrgId: string | number) => {
    if (!secOrgId) return;

    try {
      const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;
      const url = new URL(
        `/api/v4/task_management/public/secondary_organizations/${secOrgId}/topics`,
        writableApi,
      ).toString();

      const { data } = await axios.get<Topic[] | { topics: Topic[] }>(url);
      fetchedTopics.value = Array.isArray(data) ? data : (data?.topics ?? []);
    } catch (error) {
      console.error('Failed to fetch contact form topics:', error);
      fetchedTopics.value = [];
    }
  };

  const contactFormTopics = computed(() => fetchedTopics.value);

  const contactFormConfiguration = computed(() => {
    const { contact_form_configuration } = globals.value;

    if (!contact_form_configuration) {
      return {
        topics: contactFormTopics.value,
        default_email: '',
        published: false,
      };
    }

    const cleanedContactConfig = { ...contact_form_configuration };
    cleanedContactConfig.topics = contactFormTopics.value;

    return cleanedContactConfig;
  });

  const clientInfo = computed(() => {
    return {
      organizations: organizations.value,
      topics: contactFormTopics.value,
      chatConfiguration: chatConfiguration.value,
      contactFormConfiguration: contactFormConfiguration.value,
      buttonsConfiguration: buttonsConfiguration.value,
      language: language.value,
      languages: languages.value,
      currentOrgName: schoolName.value,
    };
  });

  return {
    chatConfiguration,
    contactFormConfiguration,
    contactFormTopics,
    buttonsConfiguration,
    clientInfo,
    fetchTopics,
  };
};
